import React, { useMemo, useState } from "react";
import * as THREE from "three";
import useViewer, {
    ViewerContext as ViewerSessionContext,
    ViewerLoader,
    ViewerTool,
} from "@/hooks/viewer/use-viewer.ts";
import { ViewerContext, ViewerContextProps } from "./viewer-context-props.ts";

export interface ViewerContextProviderProps {
    children: React.ReactNode;
    loaders?: ViewerLoader[];
    tools?: ViewerTool[];
    backgroundColor?: THREE.Color;
    forceResize?: boolean;
    onAllLoadersLoaded?: (ctx: ViewerSessionContext) => void | Promise<void>;
}

export const ViewerContextProvider: React.FC<ViewerContextProviderProps> = ({
    children,
    loaders = [],
    tools = [],
    backgroundColor,
    forceResize = true,
    onAllLoadersLoaded,
}) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    const {viewer, world, isLoading, loadingProgress, error, retry,
        getTool, getToolByKeyName,
        getLoader, getLoaders,
        getLoaderByKeyName, getLoadersByKeyName,
    } = useViewer({
        container, forceResize, viewerLoaders: loaders, tools, backgroundColor, onAllLoadersLoaded,
    });

    const value = useMemo<ViewerContextProps>(
        () => ({
            viewer, world, isLoading, loadingProgress, error, retry, setContainer,
            getTool, getToolByKeyName,
            getLoader, getLoaders,
            getLoaderByKeyName, getLoadersByKeyName,
        }),
        [
            viewer, world, isLoading, loadingProgress, error, retry, setContainer,
            getTool, getToolByKeyName,
            getLoader, getLoaders,
            getLoaderByKeyName, getLoadersByKeyName,
        ]
    );

    return (
        <ViewerContext.Provider value={value}>
            {children}
        </ViewerContext.Provider>
    );
};

export default ViewerContextProvider;
